import React from "react";
import { COMPANY_INFO } from "../../../data/company";
import SectionHeader from "../../common/SectionHeader/SectionHeader";
import AnimatedSection from "../../common/AnimatedSection/AnimatedSection";
import Card from "../../ui/Card/Card";
import { COMPANY_STATS } from "../../../data/products";
import "./AboutSection.css";
import { useLanguage } from "../../../context/LanguageContext";

// Motor temalı ikonlar için yardımcı fonksiyon
const getFeatureIcon = (index: number): string => {
  const icons = [
    "fas fa-cogs", // 25+ Yıllık Deneyim - Motor dişlileri
    "fas fa-tools", // Geniş Ürün Yelpazesi - Aletler
    "fas fa-shipping-fast", // Türkiye Geneli Hizmet - Hızlı teslimat
    "fas fa-award" // Kalite Garantisi - Ödül
  ];
  return icons[index] || "fas fa-cog";
};

export const AboutSection: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section id="about" className="about">
      <div className="container">
        <AnimatedSection animation="fadeInUp" delay={0.2}>
          <SectionHeader
            subtitle={t('about.subtitle')}
            title={t('about.title')}
            description={t('about.header.desc')}
          />
        </AnimatedSection>

        <div className="about__content">
          {/* Üretim Vurgusu */}
          <AnimatedSection animation="fadeInUp" delay={0.25}>
            <div className="about__experience-highlight">
              <div className="about__experience-number">CNC</div>
              <div className="about__experience-text">{t('about.experience')}</div>
              <div className="about__experience-description">
                {t('about.experience.desc')}
              </div>
            </div>
          </AnimatedSection>

          {/* Başarı İstatistikleri */}
          <AnimatedSection animation="fadeInUp" delay={0.27}>
            <div className="about__success-stats">
              {COMPANY_STATS.map((stat) => (
                <div className="about__stat-card" key={stat.id}>
                  <div className="about__stat-number">{stat.number}</div>
                  <div className="about__stat-label">{t(`hero.stats.${stat.id}`)}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Company Story */}
          <AnimatedSection animation="fadeInUp" delay={0.3}>
            <div className="about__story about__engine-parts">
              <div className="about__story-content about__technical-drawing">
                <h3 className="about__story-title">{t('about.story')}</h3>
                <p className="about__story-text">{t('about.story.text1')}</p>
                <p className="about__story-text">{t('about.story.text2')}</p>
              </div>

              <div className="about__story-image">
                <div className="about__image-placeholder about__engine-schematic">
                  <i className="fas fa-cogs"></i>
                  <p>{t('about.experience')}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Features Grid */}
          <AnimatedSection animation="fadeInUp" delay={0.4} className="animated-section--staggered">
            <div className="about__features">
              {COMPANY_INFO.features.map((feature, index) => (
                <div key={index} className="animated-section__item">
                  <Card className="about__feature-card">
                    <div className="about__feature-icon">
                      <i className={getFeatureIcon(index)}></i>
                    </div>
                    <h4 className="about__feature-title">{t(`about.features.${index}.title`)}</h4>
                    <p className="about__feature-description">{t(`about.features.${index}.desc`)}</p>
                  </Card>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Values Section */}
          <AnimatedSection animation="fadeInUp" delay={0.5}>
            <div className="about__values about__engine-parts">
              <div className="about__values-content about__technical-drawing">
                <h3 className="about__values-title">{t('about.values')}</h3>

                <div className="about__value">
                  <div className="about__value-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className="about__value-content">
                    <h4>{t('about.values.reliability')}</h4>
                    <p>{t('about.values.reliability.desc')}</p>
                  </div>
                </div>

                <div className="about__value">
                  <div className="about__value-icon">
                    <i className="fas fa-rocket"></i>
                  </div>
                  <div className="about__value-content">
                    <h4>{t('about.values.innovation')}</h4>
                    <p>{t('about.values.innovation.desc')}</p>
                  </div>
                </div>

                <div className="about__value">
                  <div className="about__value-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="about__value-content">
                    <h4>{t('about.values.customer')}</h4>
                    <p>{t('about.values.customer.desc')}</p>
                  </div>
                </div>
              </div>

              <div className="about__values-visual about__engine-schematic">
                <div className="about__vision-card about__technical-drawing">
                  <h4>{t('about.vision')}</h4>
                  <p>{t('about.vision.desc')}</p>
                </div>

                <div className="about__mission-card about__technical-drawing">
                  <h4>{t('about.mission')}</h4>
                  <p>{t('about.mission.desc')}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Timeline */}
          <AnimatedSection animation="fadeInUp" delay={0.6}>
            <div className="about__timeline">
              <h3 className="about__timeline-title">{t('about.timeline')}</h3>

              <div className="about__timeline-items">
                <div className="about__timeline-item">
                  <div className="about__timeline-year">2017</div>
                  <div className="about__timeline-content">
                    <h4>{t('about.timeline.2017.title')}</h4>
                    <p>{t('about.timeline.2017.desc')}</p>
                  </div>
                </div>

                <div className="about__timeline-item">
                  <div className="about__timeline-year">2022</div>
                  <div className="about__timeline-content">
                    <h4>{t('about.timeline.2022.title')}</h4>
                    <p>{t('about.timeline.2022.desc')}</p>
                  </div>
                </div>

                <div className="about__timeline-item">
                  <div className="about__timeline-year">Bugün</div>
                  <div className="about__timeline-content">
                    <h4>{t('about.timeline.today.title')}</h4>
                    <p>{t('about.timeline.today.desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
